'use strict'

const createEthRpcCache = require('eth-rpc-cache')
const debug = require('debug')('web3-provider-patchers')
const ethSigUtil = require('eth-sig-util')
const pRetry = require('p-retry')
const pThrottle = require('p-throttle')

const { promisify } = require('util')

// Patches an Ethereum provider so it has a `request` method as specified by
// EIP-1193.
const toEip1193 = function (provider) {
  // If the request method is present, do nothing.
  if (typeof provider.request === 'function') {
    return provider
  }

  // Otherwise, test if the legacy methods exist.
  const sendAsync = provider.sendAsync || provider.send
  if (!sendAsync || typeof sendAsync !== 'function') {
    throw new Error('Provider does not have a sendAsync or send method to use')
  }

  // And then patch the provider. The legacy methods receive a JSON-RPC request
  // and a callback, and return a JSON-RPC response. Wrap and un-wrap is needed.
  const _send = promisify(sendAsync.bind(provider))
  let seq = 1
  provider.request = function ({ method, params }) {
    const id = seq++
    const payload = { jsonrpc: '2.0', method, params, id }
    return _send(payload).then(function (response) {
      if (response.jsonrpc !== '2.0' || response.id !== id) {
        throw new Error('Invalid JSON-RPC response')
      }
      if (response.error) {
        const { code, message, data } = response.error
        const error = new Error(message)
        Object.assign(error, { code, data })
        throw error
      }
      return response.result
    })
  }

  // Legacy providers also emit messages with a different format. Instead of the
  // `message` event and format, use a `data` event with a different payload.
  if (provider.emit) {
    const _emit = provider.emit.bind(provider)
    provider.emit = function (message, data) {
      if (message === 'data') {
        _emit('message', { type: data.method, data: data.params })
        return
      }
      _emit(message, data)
    }
  }

  return provider
}

// Under some circumstances of heavy load p-throttle does not properly limit
// the concurrent calls. It should be patched, replaced or reimplemented.
function throttle(provider, options) {
  const throttler = pThrottle(options)
  const newProvider = {
    ...provider,
    request: throttler(provider.request)
  }
  Object.setPrototypeOf(newProvider, Object.getPrototypeOf(provider))
  return newProvider
}

const signTypedData = function (provider, getPrivateKey) {
  const newProvider = {
    ...provider,
    request({ method, params }) {
      if (
        method === 'eth_signTypedData_v3' ||
        method === 'eth_signTypedData_v4'
      ) {
        const [signer, jsonData] = params
        const privateKey = getPrivateKey(signer)
        const data = JSON.parse(jsonData)
        const signature = ethSigUtil.signTypedData(privateKey, { data })
        return Promise.resolve(signature)
      }
      return provider.request({ method, params })
    }
  }
  Object.setPrototypeOf(newProvider, Object.getPrototypeOf(provider))
  return newProvider
}

// Log all calls made and their results.
const logger = function (provider, fullLogs) {
  let seq = 1
  const newProvider = {
    ...provider,
    request: fullLogs
      ? function ({ method, params }) {
          const id = seq++
          debug('--> %s %s %s', id, method, JSON.stringify(params))
          return provider
            .request({ method, params })
            .then(function (response) {
              debug('<-- %s %s', id, JSON.stringify(response))
              return response
            })
            .catch(function (err) {
              debug('<-- %s %s', id, err.message)
              throw err
            })
        }
      : function ({ method, params }) {
          const id = seq++
          debug('--> %s %s', id, method)
          return provider
            .request({ method, params })
            .then(function (response) {
              debug('<-- %s OK', id)
              return response
            })
            .catch(function (err) {
              debug('<-- %s FAILED %s', id, err.message)
              throw err
            })
        }
  }
  Object.setPrototypeOf(newProvider, Object.getPrototypeOf(provider))
  return newProvider
}

// Retry failing calls.
function retry(provider, options) {
  const newProvider = {
    ...provider,
    request: ({ method, params }) =>
      pRetry(() => provider.request({ method, params }), {
        onFailedAttempt(err) {
          if (err.code && err.message.includes('revert')) {
            // It is a contract error. Do not retry.
            throw err
          }
          debug('Retrying failed %s call: %s', method, err.message)
        },
        retries: 3,
        ...options
      })
  }
  Object.setPrototypeOf(newProvider, Object.getPrototypeOf(provider))
  return newProvider
}

// Cache calls using the ETH RPC cache module and strategies.
function cache(provider, options) {
  const cached = createEthRpcCache(
    (method, params) => provider.request({ method, params }),
    options
  )
  const newProvider = {
    ...provider,
    request: ({ method, params }) => cached(method, params)
  }
  Object.setPrototypeOf(newProvider, Object.getPrototypeOf(provider))
  return newProvider
}

module.exports = {
  cache,
  logger,
  retry,
  signTypedData,
  throttle,
  toEip1193
}
